/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps } from 'preact';
import { useCallback, useLayoutEffect, useRef } from 'preact/hooks';
import type { Dispatch, MutableRef } from 'preact/hooks';
import type { TimeReducerAction } from './timeReducer';
import type { TimeFieldState } from './getTimeFieldInitialState';
import type { Time } from './types';
import type { InputTimeMask } from './InputTimeMask';
import { timesAreDifferent } from '#utils/UNSAFE_timeUtils';

type InputTimeMaskProps = ComponentProps<typeof InputTimeMask>;

type Props = {
  timeResetRef: MutableRef<boolean>;
  dispatch: Dispatch<TimeReducerAction>;
  onCommit: InputTimeMaskProps['onCommit'];
  onInput: InputTimeMaskProps['onInput'];
  state: TimeFieldState;
  value?: Time;
};

/**
 * A hook that manages the onInput and onCommit in a useLayoutEffect. The reason for the
 * useLayoutEffect is we do not want to call onInput or onCommit during the render cycle.
 *
 * The timeResetRef is used to track whether the time value has been programmatically changed
 * (i.e., reset) from outside the component.
 *
 * When timeResetRef.current is true, it indicates that the time value has been reset, and the hook
 * uptimes the lastTimeRef an the lastCommittedTimeRef to the new value.
 * The hook checks timeResetRef.current to be false before calling onInput. This prevents onInput
 * from being called when the time value is reset programmatically. Then timeResetRef.current is set to false.
 *
 * timeResetRef.current is set to true in useTimeFieldState.
 */
const useTimeFieldCommitInput = ({
  timeResetRef,
  dispatch,
  onCommit,
  onInput,
  state,
  value: currentValue
}: Props) => {
  // Keep track of the last changed and last committed time values.
  const lastTimeRef = useRef<Time | undefined>(state.currentTime);
  const lastCommittedTimeRef = useRef<Time | undefined>(state.currentTime);

  // If the value was programmatically changed, then uptime the
  // last changed and last committed time refs to stay in sync.
  // currentValue is the controlled value passed from the parent.
  if (timeResetRef.current) {
    lastTimeRef.current = currentValue;
    lastCommittedTimeRef.current = currentValue;
  }

  // Call onCommit, only if the value is different.
  const doCommitIfNeeded = useCallback(() => {
    if (timesAreDifferent(lastCommittedTimeRef.current, state.currentTime)) {
      onCommit?.({ value: state.currentTime });
      lastCommittedTimeRef.current = state.currentTime;
    }
  }, [onCommit, state.currentTime]);

  // If you try to call onInput from onChange directly it doesn't work. The value of
  // state.currentTime is from the current render, not the render *after* the dispatch.
  // So wait for the state to finish updating.
  useLayoutEffect(() => {
    // do not call onInput if the time has been reset programmatically
    if (timesAreDifferent(lastTimeRef.current, state.currentTime) && !timeResetRef.current) {
      const frozenTime =
        state.currentTime !== undefined
          ? Object.freeze({ ...state.currentTime })
          : state.currentTime;
      onInput({ value: frozenTime });
      lastTimeRef.current = frozenTime;
    }
    timeResetRef.current = false;
  }, [onInput, state.currentTime, timeResetRef]);

  // Check state.isCommitting which is set when we are done updating the state's value
  // on Enter or Blur and ready to call onCommit. We do not want to call onCommit during render,
  // so we use useLayoutEffect. useEffect might trigger this issue - JET-73689 oj-c-input-time-picker onValueChange triggered after button submit function.
  useLayoutEffect(() => {
    if (state.isCommitting) {
      doCommitIfNeeded();
      // This will uptime the state.isCommitting to false.
      dispatch({ actionType: 'committed' });
    }
  }, [dispatch, doCommitIfNeeded, state.isCommitting]);

  return;
};

export { useTimeFieldCommitInput };
